// @/services/app/store/modules/terms-acceptance.js

/**
 * TermsAcceptanceHandler
 *
 * Per-user, blocking legal gate. Pairs with the backend
 * `/api/terms-acceptance/*` endpoints (api#49+).
 *
 *   - GET  /api/terms-acceptance/check   → { shouldShow, currentVersion?, termsUrl?, privacyUrl? }
 *   - POST /api/terms-acceptance/accept  → empty body; server derives userId + currentVersion
 *
 * Authorization is the user's Keycloak token (NOT GATSBY_STRAPI_TOKEN). Server
 * extracts `sub` via the decode-keycloak-user policy.
 *
 * localStorage caching: the version-scoped `termsAccepted_<version>` key is
 * monotonic — once a version is accepted, the cache cannot become stale. A
 * new version invalidates the key automatically (different cache key).
 *
 * Behaviour on network failure: fail-open (return shouldShow=false). The
 * platform should never become unreachable due to a backend hiccup; the next
 * visibilitychange retry catches the user if they hadn't actually accepted.
 */

import { authService } from '@/services/auth';

const ACCEPTED_KEY_PREFIX = 'termsAccepted_';

export class TermsAcceptanceHandler {
  constructor(store) {
    this.store = store;
    this.checkAbortController = null;
    this.acceptAbortController = null;
  }

  _authHeader() {
    const token = authService.getAuthState()?.token;
    return token ? { Authorization: `Bearer ${token}` } : null;
  }

  /**
   * Check whether the current user must accept the current terms version.
   *
   * @param {string} locale
   * @returns {Promise<{shouldShow: boolean, currentVersion?: string, termsUrl?: string, privacyUrl?: string, reason?: string}>}
   */
  async check(locale = 'en') {
    const headers = this._authHeader();
    if (!headers) {
      console.log('[TermsGate] check skip reason=no_auth_token');
      return { shouldShow: false, reason: 'no_auth_token' };
    }

    if (this.checkAbortController) this.checkAbortController.abort();
    this.checkAbortController = new AbortController();

    try {
      const url = `${process.env.GATSBY_STRAPI_URL}/api/terms-acceptance/check?locale=${encodeURIComponent(locale)}`;
      const response = await fetch(url, {
        headers: { ...headers, Accept: 'application/json' },
        signal: this.checkAbortController.signal,
        cache: 'no-cache',
      });

      if (!response.ok) {
        console.warn(`[TermsGate] check fail-open status=${response.status}`);
        return { shouldShow: false, reason: 'fetch_non_ok' };
      }

      const data = await response.json();
      console.log('[TermsGate] check response', data);

      // Only mirror to localStorage when the server confirms acceptance.
      // Don't pre-cache shouldShow=true — gate must always re-check on focus.
      if (data?.shouldShow === false && data?.reason === 'already_accepted' && data?.acceptedVersion) {
        try {
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem(`${ACCEPTED_KEY_PREFIX}${data.acceptedVersion}`, 'true');
          }
        } catch {
          // Storage quota — ignore.
        }
      }

      return data;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.warn('[TermsGate] check fail-open after error', error);
      }
      return { shouldShow: false, reason: 'fetch_error' };
    } finally {
      this.checkAbortController = null;
    }
  }

  /**
   * Record acceptance of the current version. Server derives userId from JWT
   * + version from the isCurrent=true row — the body is empty by design.
   *
   * @returns {Promise<{recorded: boolean, version?: string, reason?: string}>}
   */
  async accept() {
    const headers = this._authHeader();
    if (!headers) {
      console.warn('[TermsGate] accept failed — no auth token');
      return { recorded: false, reason: 'no_auth_token' };
    }

    if (this.acceptAbortController) this.acceptAbortController.abort();
    this.acceptAbortController = new AbortController();

    try {
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/terms-acceptance/accept`,
        {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
          signal: this.acceptAbortController.signal,
        }
      );

      if (!response.ok) {
        console.warn(`[TermsGate] accept non-ok status=${response.status}`);
        return { recorded: false, reason: 'fetch_non_ok' };
      }

      const json = await response.json();
      const result = json?.data || {};
      if (result.version && typeof localStorage !== 'undefined') {
        try {
          localStorage.setItem(`${ACCEPTED_KEY_PREFIX}${result.version}`, 'true');
        } catch {
          // Storage quota — ignore.
        }
      }
      console.log('[TermsGate] accept recorded', result);
      return { recorded: true, ...result };
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[TermsGate] accept error', error);
      }
      return { recorded: false, reason: 'fetch_error' };
    } finally {
      this.acceptAbortController = null;
    }
  }

  cleanup() {
    if (this.checkAbortController) this.checkAbortController.abort();
    if (this.acceptAbortController) this.acceptAbortController.abort();
    this.checkAbortController = null;
    this.acceptAbortController = null;
  }
}
